import { Inject, Injectable, Logger } from '@nestjs/common';
import {
  IMAGE_STORAGE_ADAPTER,
  ImageStorageAdapter,
  PrismaService,
} from '@cart/api-data-access';
import { purgeProductImage } from './image-purge.util';

// How long a soft-deleted (trashed) product image survives before this
// job hard-deletes it and frees the merchant's storage quota. A merchant
// can also purge earlier themselves via the Trash panel's "Delete Now".
const TRASH_RETENTION_DAYS = Number(process.env.IMAGE_TRASH_RETENTION_DAYS ?? 30);

@Injectable()
export class ImageTrashPurgeService {
  private readonly logger = new Logger(ImageTrashPurgeService.name);

  constructor(
    private readonly prisma: PrismaService,
    @Inject(IMAGE_STORAGE_ADAPTER) private readonly storageAdapter: ImageStorageAdapter
  ) {}

  /*
   * NOT SCHEDULED — superseded by MediaTrashService.sweep().
   *
   * This swept `ProductImage.isActive = false`, which is a catalogue row. The
   * object in storage belongs to a MediaAsset, and this cron never touched
   * those: it deleted the file and the catalogue row while leaving the asset
   * counting bytes forever. The replacement sweeps assets, which is what
   * actually occupies storage, and releases the quota as it goes.
   *
   * Kept, unscheduled, for images that predate the media registry and have no
   * `mediaAssetId` — `purgeLegacyTrash()` can still be called by hand for
   * those. Delete this file once none remain.
   *
   * Re-enabling means restoring both `@Cron('0 3 * * *')` here AND the `Cron`
   * import from `@nestjs/schedule`; the import was dropped rather than left
   * unused, since a commented-out decorator is not a reason to keep a live
   * import that every lint run flags.
   */
  async purgeLegacyTrash(): Promise<void> {
    const cutoff = new Date(Date.now() - TRASH_RETENTION_DAYS * 24 * 60 * 60 * 1000);
    const expired = await this.prisma.productImage.findMany({
      where: { isActive: false, trashedAt: { lt: cutoff }, mediaAssetId: null },
      select: { id: true, storageKey: true },
    });
    if (!expired.length) return;

    let purged = 0;
    for (const image of expired) {
      try {
        await purgeProductImage(this.prisma, this.storageAdapter, image);
        purged += 1;
      } catch (err) {
        this.logger.warn(`Failed to purge trashed image ${image.id}: ${err}`);
      }
    }

    if (purged > 0) {
      this.logger.log(`Purged ${purged} trashed image(s) older than ${TRASH_RETENTION_DAYS}d`);
    }
  }
}
